import { dedupeArray, flattenArray, isDefined } from '@showroomjs/core';
import { Checkbox, Collapsible, pulse, styled, Tabs } from '@showroomjs/ui';
import * as React from 'react';
import { useA11yResult } from '../lib/use-a11y-result';
import { A11yResultItem, Root as ItemRoot } from './a11y-result-item';
export const A11yResultPanel = (props) => {
    const { result } = useA11yResult();
    const [activeTab, setActiveTab] = React.useState(() => {
        if (result) {
            return getDefaultTab(result);
        }
    });
    const allSelectors = React.useMemo(() => {
        if (!result || !activeTab) {
            return [];
        }
        const items = activeTab === 'violations'
            ? result.violations
            : activeTab === 'passes'
                ? result.passes
                : result.incomplete;
        return dedupeArray(flattenArray(items.map((item) => item.nodes.map((node) => node.target[0]))).filter(isDefined));
    }, [result, activeTab]);
    const [highlightedItems, setHighlightedItems] = React.useState([]);
    const toggleHighlight = React.useCallback((selector) => {
        setHighlightedItems((prev) => prev.includes(selector)
            ? prev.filter((s) => s !== selector)
            : prev.concat(selector));
    }, []);
    React.useEffect(() => {
        if (activeTab) {
            props.highlightItems(highlightedItems, colorByType[activeTab] || '');
        }
    }, [highlightedItems]);
    const checkStatus = React.useMemo(() => {
        if (allSelectors.length === 0) {
            return 'indeterminate';
        }
        return allSelectors.every((s) => highlightedItems.includes(s))
            ? true
            : allSelectors.some((s) => highlightedItems.includes(s))
                ? 'indeterminate'
                : false;
    }, [allSelectors, highlightedItems]);
    React.useEffect(() => {
        if (result) {
            const tab = getDefaultTab(result);
            if (tab) {
                setActiveTab((prevTab) => prevTab || tab);
            }
        }
    }, [result]);
    return (React.createElement(Tabs.Root, { value: activeTab, onValueChange: (newTab) => {
            setActiveTab(newTab);
            setHighlightedItems([]);
        } },
        React.createElement(TabNav, null,
            React.createElement(Tabs.RawList, null,
                React.createElement(TabTrigger, { value: "violations" },
                    "Violations ",
                    React.createElement(Count, null, result ? result.violations.length : '?')),
                React.createElement(TabTrigger, { value: "passes" },
                    "Passes ",
                    React.createElement(Count, null, result ? result.passes.length : '?')),
                React.createElement(TabTrigger, { value: "incompletes" },
                    "Incomplete ",
                    React.createElement(Count, null, result ? result.incomplete.length : '?'))),
            React.createElement(CheckboxLabel, null,
                React.createElement("span", null, "Highlight"),
                React.createElement(Checkbox, { checked: checkStatus, onCheckedChange: (allChecked) => {
                        setHighlightedItems(allChecked ? allSelectors : []);
                    }, disabled: allSelectors.length === 0 }))),
        result ? (React.createElement(React.Fragment, null,
            React.createElement(TabContent, { value: "violations" }, result.violations.length > 0 ? (React.createElement(ResultItemList, null, result.violations.map((p) => (React.createElement(A11yResultItem, { result: p, type: "violation", highlightedItems: highlightedItems, setHighlightedItems: setHighlightedItems, toggleItem: toggleHighlight, key: p.id }))))) : (React.createElement(NoItemMsg, null, "No applicable item"))),
            React.createElement(TabContent, { value: "passes" }, result.passes.length > 0 ? (React.createElement(ResultItemList, null, result.passes.map((p) => (React.createElement(A11yResultItem, { result: p, type: "pass", highlightedItems: highlightedItems, setHighlightedItems: setHighlightedItems, toggleItem: toggleHighlight, key: p.id }))))) : (React.createElement(NoItemMsg, null, "No applicable item"))),
            React.createElement(TabContent, { value: "incompletes" }, result.incomplete.length > 0 ? (React.createElement(ResultItemList, null, result.incomplete.map((p) => (React.createElement(A11yResultItem, { result: p, type: "incomplete", highlightedItems: highlightedItems, setHighlightedItems: setHighlightedItems, toggleItem: toggleHighlight, key: p.id }))))) : (React.createElement(NoItemMsg, null, "No applicable item"))))) : (React.createElement(LoadingPlaceholderList, null))));
};
const LoadingPlaceholderList = () => (React.createElement(ResultItemList, { css: {
        backgroundColor: 'White',
        animation: `${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite`,
    } },
    React.createElement(PlaceholderItem, { width: "60%" }),
    React.createElement(PlaceholderItem, { width: "70%" }),
    React.createElement(PlaceholderItem, { width: "50%" })));
const ResultItemList = styled('ul', {
    listStyle: 'none',
    margin: 0,
    padding: 0,
});
const LoadingLabel = styled('div', {
    backgroundColor: '$gray-300',
    height: '1.25rem',
});
const PlaceholderItemInner = styled('div', {
    display: 'flex',
    alignItems: 'center',
    paddingRight: '$4',
    paddingLeft: '$1',
});
const PlaceholderItem = ({ width }) => (React.createElement(ItemRoot, null,
    React.createElement(PlaceholderItemInner, null,
        React.createElement(Collapsible.ToggleIcon, { direction: "right", width: 16, height: 16, css: {
                marginRight: '$1',
            } }),
        React.createElement(LoadingLabel, { css: {
                width,
            } }))));
const Count = styled('span', {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '$full',
    fontSize: '$xs',
    lineHeight: 1,
    width: 14,
    height: 14,
    backgroundColor: '$gray-500',
    color: 'White',
});
const getDefaultTab = (result) => {
    if (result.violations.length > 0) {
        return 'violations';
    }
    if (result.passes.length > 0) {
        return 'passes';
    }
    if (result.incomplete.length > 0) {
        return 'incompletes';
    }
};
const colorByType = {
    violations: '#EF4444',
    passes: '#10B981',
    incompletes: '#F59E0B',
};
const TabTrigger = styled(Tabs.RawTrigger, {
    display: 'inline-flex',
    alignItems: 'center',
    px: '$2',
    py: '$1',
    fontSize: '$sm',
    lineHeight: '$sm',
    gap: '$1',
    border: 0,
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    backgroundColor: 'inherit',
    outlineRing: '',
    '&[data-state="active"]': {
        borderColor: '$primary-800',
    },
});
const CheckboxLabel = styled('label', {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '$2',
    fontSize: '$sm',
    lineHeight: '$sm',
    color: '$gray-700',
    px: '$4',
});
const TabNav = styled('div', {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});
const TabContent = styled(Tabs.RawContent, {
    backgroundColor: 'White',
});
const NoItemMsg = styled('p', {
    px: '$2',
    py: '$1',
    margin: 0,
    color: '$gray-600',
    fontSize: '$sm',
    lineHeight: '$sm',
});
//# sourceMappingURL=a11y-result-panel.js.map